import { Injectable } from '@angular/core'
import { HttpHeaders } from '@angular/common/http'

import { PageInfo } from '../common'
import { ADMIN_API_ENDPOINTS } from '../common/api-endpoints'
import { HTTP_HEADER_MALL_SLUG_KEY } from '../core/constants'
import {
  FileDto,
  FloorMapManagementDto,
  FloorMapInteractiveFloorOptionDto,
  FloorMapInteractiveModeDto,
  PageResultDto
} from '../dto'
import { HttpService } from './http.service'

@Injectable({
  providedIn: 'root'
})
export class AdminFloorMapManagementService {

  constructor(private httpService: HttpService) {}

  public async list(
    mallId?: string,
    floorId?: string,
    page: number = 1,
    pageSize: number = 10
  ): Promise<{ rows: FloorMapManagementDto[]; count: number; pageInfo: PageInfo }> {
    const queryParams: any = {
      limit: pageSize,
      offset: (page - 1) * pageSize
    }
    if (mallId) queryParams.mallId = mallId
    if (floorId) queryParams.floorId = floorId

    const result = await this.httpService.get<PageResultDto<FloorMapManagementDto>>(
      ADMIN_API_ENDPOINTS.FLOOR_MAP_INTERACTIVE,
      queryParams
    )

    const pageInfo = PageInfo.create()
    pageInfo.page = page
    pageInfo.pageSize = pageSize
    pageInfo.collectionSize = result.count

    return { rows: result.rows, count: result.count, pageInfo }
  }

  public async getById(id: string): Promise<FloorMapManagementDto> {
    return await this.httpService.get<FloorMapManagementDto>(`${ADMIN_API_ENDPOINTS.FLOOR_MAP_INTERACTIVE}/${id}`)
  }

  /**
   * PATCH .../{id}/static-map takes the image itself as multipart/form-data
   * (field `file`) and returns the stored file — no separate upload call.
   */
  public async setStaticMap(id: string, file: File): Promise<FileDto> {
    const formData = new FormData()
    formData.append('file', file)

    return await this.httpService.patch<FileDto>(
      `${ADMIN_API_ENDPOINTS.FLOOR_MAP_INTERACTIVE}/${id}/static-map`,
      formData
    )
  }

  public async getFloors(mallId: string): Promise<FloorMapInteractiveFloorOptionDto[]> {
    return await this.httpService.get<FloorMapInteractiveFloorOptionDto[]>(
      `${ADMIN_API_ENDPOINTS.FLOOR_MAP_INTERACTIVE}/floors`,
      { mallId }
    )
  }

  public async getInteractiveMode(mallSlug: string): Promise<boolean> {
    const headers = new HttpHeaders().set(HTTP_HEADER_MALL_SLUG_KEY, mallSlug)
    const result = await this.httpService.get<FloorMapInteractiveModeDto>(
      `${ADMIN_API_ENDPOINTS.FLOOR_MAP_INTERACTIVE}/mode`,
      undefined,
      headers
    )
    return result?.floorMapInteractiveMode || false
  }

  public async setInteractiveMode(mallSlug: string, enabled: boolean): Promise<void> {
    const headers = new HttpHeaders().set(HTTP_HEADER_MALL_SLUG_KEY, mallSlug)
    await this.httpService.put<boolean>(
      `${ADMIN_API_ENDPOINTS.FLOOR_MAP_INTERACTIVE}/mode`,
      { floorMapInteractiveMode: enabled },
      undefined,
      headers
    )
  }
}
